// Drawer: requirement classification result (actor/action/object + gaps).
import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet";
import type { RequirementClassifyResult } from "../api/contentRegistryApi";
import { type RequirementTabKey } from "../constants";
import {
  RequirementDrawerHeader,
  StatusBanner,
} from "./requirementIntelligenceParts";
import {
  ExtractionSummarySection,
  ObligationStrengthSection,
  RequirementsListSection,
} from "./requirementIntelligenceSections";
import {
  buildBannerText,
  buildDonutSlices,
  computeKindCounts,
  computeTakeaway,
  decomposedToneClass,
  deriveByModal,
  filterByTab,
  resolveStatusKind,
  safeCount,
  REQUIREMENT_DRAWER_COPY,
  REQUIREMENT_DRAWER_OVERLAY_CLS,
  REQUIREMENT_DRAWER_SHEET_CLS,
} from "./utilities";

export interface RequirementIntelligenceDrawerProps {
  open: boolean;
  onClose: () => void;
  result: RequirementClassifyResult | null;
  documentTitle?: string;
  documentType?: string;
  frameworkLabel?: string;
}

export function RequirementIntelligenceDrawer({
  open,
  onClose,
  result,
  documentTitle,
  documentType,
  frameworkLabel,
}: RequirementIntelligenceDrawerProps) {
  // ── Local UI state (grouped at top) ─────────────────────────────
  const [tab, setTab] = useState<RequirementTabKey>("all");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [page, setPage] = useState(1);

  // ── Handlers ────────────────────────────────────────────────────
  const resetDrawerState = () => {
    setTab("all");
    setExpanded(new Set());
    setPage(1);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    resetDrawerState();
    onClose();
  };

  const handleTabChange = (next: RequirementTabKey) => {
    setTab(next);
    setPage(1);
    setExpanded(new Set());
  };

  const handleToggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── Derived data ────────────────────────────────────────────────
  const findings = useMemo(() => result?.findings ?? [], [result]);
  const metrics = result?.metrics;
  const backendSummary = result?.summary;

  const total = safeCount(metrics?.total, findings.length);
  const fullyDecomposed = safeCount(
    metrics?.fully_decomposed,
    findings.filter((f) => f.decomposed === true).length,
  );
  const notDecomposed = safeCount(
    metrics?.not_decomposed,
    Math.max(0, total - fullyDecomposed),
  );
  const clausesSeen = safeCount(metrics?.clauses_seen);
  const clausesWithoutModal = safeCount(metrics?.clauses_without_modal);

  const decomposedPct = total > 0 ? (fullyDecomposed / total) * 100 : 0;
  const decomposedTone = decomposedToneClass(decomposedPct);

  const byModal = useMemo(
    () => deriveByModal(metrics?.by_modal, findings),
    [metrics, findings],
  );
  const donutSlices = useMemo(() => buildDonutSlices(byModal), [byModal]);
  const modalTotal = useMemo(
    () => donutSlices.reduce((n, s) => n + s.count, 0),
    [donutSlices],
  );

  const kindCounts = useMemo(() => computeKindCounts(findings), [findings]);
  const takeaway = useMemo(() => computeTakeaway(kindCounts), [kindCounts]);
  const flaggedCount = useMemo(
    () => findings.filter((f) => f.flagged === true).length,
    [findings],
  );

  const filteredFindings = useMemo(
    () => filterByTab(findings, tab),
    [findings, tab],
  );

  const statusKind = resolveStatusKind(result?.status, notDecomposed);
  const bannerText = useMemo(
    () => buildBannerText(statusKind, total, fullyDecomposed, notDecomposed),
    [statusKind, total, fullyDecomposed, notDecomposed],
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        overlayClassName={REQUIREMENT_DRAWER_OVERLAY_CLS}
        className={REQUIREMENT_DRAWER_SHEET_CLS}
      >
        <SheetTitle className="sr-only">
          {REQUIREMENT_DRAWER_COPY.title}
        </SheetTitle>
        <SheetDescription className="sr-only">
          {REQUIREMENT_DRAWER_COPY.description}
        </SheetDescription>

        {!result ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
            <span className="text-sm">{REQUIREMENT_DRAWER_COPY.loading}</span>
          </div>
        ) : (
          <>
            <RequirementDrawerHeader
              documentTitle={documentTitle}
              documentType={documentType}
              frameworkLabel={frameworkLabel}
              backendSummary={backendSummary}
              total={total}
              decomposedPct={decomposedPct}
              decomposedTone={decomposedTone}
              statusKind={statusKind}
            />

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-4">
              <StatusBanner notDecomposed={notDecomposed} text={bannerText} />

              <ExtractionSummarySection
                total={total}
                fullyDecomposed={fullyDecomposed}
                notDecomposed={notDecomposed}
                clausesSeen={clausesSeen}
                clausesWithoutModal={clausesWithoutModal}
              />

              <ObligationStrengthSection
                total={total}
                slices={donutSlices}
                modalTotal={modalTotal}
                takeaway={takeaway}
              />

              <RequirementsListSection
                findings={findings}
                filteredFindings={filteredFindings}
                mandatoryCount={kindCounts.mandatory}
                prohibitedCount={kindCounts.prohibited}
                flaggedCount={flaggedCount}
                tab={tab}
                onTabChange={handleTabChange}
                page={page}
                onPageChange={setPage}
                expanded={expanded}
                onToggleExpand={handleToggleExpand}
              />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
